import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import axiosInstance from 'api/config/axios';
import { ProductPropertyTypeCode, TradeStatusAttributeCode } from 'utils/enums';
import { memoryQueryClient } from 'providers/TanstackQuery';
import { useDataValues } from 'providers/DataValues';
import { AimeosEntityType, AimeosManyResponse, AimeosProduct, AimeosProductType } from '../../types';
import { parseManyResponse } from '../../parsers';

type Params = {
  completedAttrId: string;
  userId: string;
};

/**
 * Fetches trades owned by the current user that have the "Completed" status.
 * Used by the watcher hook to detect newly completed trades and notify the seller.
 */
async function getMyCompletedTrades({ userId, completedAttrId }: Params) {
  const params = {
    sort: 'product.mtime',
    fields: {
      product: 'product.id,product.mtime,product.code',
    },
    filter: {
      '&&': [
        { '==': { 'product.status': 1 } },
        { '==': { 'product.type': AimeosProductType.Trade } },
        { '!=': { [`product:prop("${ProductPropertyTypeCode.TradeCreator}",null,"${userId}")`]: null } },
        { '!=': { [`product:has("attribute","default","${completedAttrId}")`]: null } },
      ],
    },
  };

  const response = await axiosInstance.query.get<AimeosManyResponse<AimeosProduct>>('/admin/default/jsonadm/product', {
    params,
  });

  return response.data;
}

const selector = (result: AimeosManyResponse<AimeosProduct>) => {
  return parseManyResponse<AimeosEntityType.Product>(result).map((item) => {
    const code = item.data.code;
    return {
      ...item,
      data: {
        ...item.data,
        code: code.includes('-trade') ? (code.split('-trade')?.[0] ?? code) : code,
      },
    };
  });
};

export const getQueryKey = (params: Params) =>
  ['trade', 'completed-trades', params.userId, params.completedAttrId] as const;

/**
 * React Query hook that fetches the current user's completed trades.
 * Mirrors `useGetMyApprovedTrades` but filters by `TradeStatusAttributeCode.Completed`.
 */
export function useGetMyCompletedTrades(enabled = true) {
  const { userId, appValues } = useDataValues();

  const completedAttrId = useMemo(
    () => appValues?.attributes?.tradeStatus?.find((a) => a.code === TradeStatusAttributeCode.Completed)?.id ?? '',
    [appValues],
  );

  const params = useMemo(() => ({ userId, completedAttrId }), [userId, completedAttrId]);

  return useQuery(
    {
      queryKey: getQueryKey(params),
      queryFn: () => getMyCompletedTrades(params),
      enabled: !!params.userId && !!params.completedAttrId && enabled,
      select: selector,
    },
    memoryQueryClient,
  );
}
